import React, { useEffect, useRef, useState } from "react";
import styles from "../Tree.module.css";

interface TreeNode {
  id: number;
  value: number;
  left: TreeNode | null;
  right: TreeNode | null;
  x?: number;
  y?: number;
}

interface BinaryTreeEngineProps {
  externalNodes?: number[];
  traversal: string | null;
  setHighlightLine: (line: number | null) => void;
  highlightedNodes: number[];
  setHighlightedNodes: React.Dispatch<React.SetStateAction<number[]>>;
  setTraversalComplete?: React.Dispatch<React.SetStateAction<boolean>>;
  resetTrigger?: number;
}

const BinaryTreeEngine: React.FC<BinaryTreeEngineProps> = ({
  externalNodes,
  traversal,
  setHighlightLine,
  highlightedNodes,
  setHighlightedNodes,
  setTraversalComplete,
  resetTrigger,
}) => {
  const [root, setRoot] = useState<TreeNode | null>(null);
  const animationTimers = useRef<NodeJS.Timeout[]>([]);
  const generateUniqueNumbers = (count: number): number[] => {
    const nums = Array.from({ length: 50 }, (_, i) => i);
    for (let i = nums.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [nums[i], nums[j]] = [nums[j], nums[i]];
    }
    return nums.slice(0, count);
  };

  useEffect(() => {
    const values =
      externalNodes && externalNodes.length > 0
        ? externalNodes
        : generateUniqueNumbers(7);

    const nodes: TreeNode[] = values.map((val, i) => ({
      id: i,
      value: val,
      left: null,
      right: null,
    }));

    for (let i = 0; i < nodes.length; i++) {
      const leftIndex = 2 * i + 1;
      const rightIndex = 2 * i + 2;
      if (leftIndex < nodes.length) nodes[i].left = nodes[leftIndex];
      if (rightIndex < nodes.length) nodes[i].right = nodes[rightIndex];
    }

    const assignPositions = (
      node: TreeNode | null,
      depth = 0,
      minX = -400,
      maxX = 400,
      levelHeight = 100
    ): void => {
      if (!node) return;
      const midX = (minX + maxX) / 2;
      const y = 80 + depth * levelHeight;
      node.x = midX;
      node.y = y;
      assignPositions(node.left, depth + 1, minX, midX, levelHeight);
      assignPositions(node.right, depth + 1, midX, maxX, levelHeight);
    };

    if (nodes.length > 0) {
      const rootNode = nodes[0];
      assignPositions(rootNode);
      setRoot(rootNode);
    }
  }, [externalNodes]);

  useEffect(() => {
    if (!resetTrigger) return;
    animationTimers.current.forEach(clearTimeout);
    animationTimers.current = [];
    setHighlightedNodes([]);
    setHighlightLine(null);
  }, [resetTrigger]);

  useEffect(() => {
    animationTimers.current.forEach(clearTimeout);
    animationTimers.current = [];
    setHighlightedNodes([]);
    setHighlightLine(null);
  }, [traversal]);

  useEffect(() => {
    if (!traversal || !root) return;

    animationTimers.current.forEach(clearTimeout);
    animationTimers.current = [];
    setHighlightedNodes([]);
    setHighlightLine(null);
    if (setTraversalComplete) setTraversalComplete(false);

    const order: TreeNode[] = [];

    const dfs = (node: TreeNode | null, type: string) => {
      if (!node) return;
      if (type === "preorder") order.push(node);
      dfs(node.left, type);
      if (type === "inorder") order.push(node);
      dfs(node.right, type);
      if (type === "postorder") order.push(node);
    };

    const bfs = (node: TreeNode | null) => {
      if (!node) return;
      const queue: TreeNode[] = [node];
      while (queue.length) {
        const current = queue.shift()!;
        order.push(current);
        if (current.left) queue.push(current.left);
        if (current.right) queue.push(current.right);
      }
    };

    if (traversal === "levelorder") bfs(root);
    else dfs(root, traversal);

    order.forEach((node, idx) => {
      const timer = setTimeout(() => {
        setHighlightedNodes((prev) => [...prev, node.value]);
        setHighlightLine(idx % 5);
        if (idx === order.length - 1 && setTraversalComplete) {
          setTraversalComplete(true);
        }
      }, idx * 700);
      animationTimers.current.push(timer);
    });

    const cleanup = setTimeout(() => {
      setHighlightLine(null);
    }, order.length * 700);
    animationTimers.current.push(cleanup);

    return () => {
      animationTimers.current.forEach(clearTimeout);
      animationTimers.current = [];
    };
  }, [traversal, root]);

  const getTreeDepth = (node: TreeNode | null): number => {
    if (!node) return 0;
    return 1 + Math.max(getTreeDepth(node.left), getTreeDepth(node.right));
  };

  const depth = getTreeDepth(root);
  const svgHeight = Math.max(450, depth * 120);
  const viewBoxHeight = Math.max(400, depth * 120);
  const nodes: TreeNode[] = [];
  const lines: { x1: number; y1: number; x2: number; y2: number; key: string }[] = [];

  const traverseNodes = (node: TreeNode | null) => {
    if (!node) return;
    nodes.push(node);
    if (node.left) {
      lines.push({
        x1: node.x!,
        y1: node.y!,
        x2: node.left.x!,
        y2: node.left.y!,
        key: `${node.id}-${node.left.id}`,
      });
      traverseNodes(node.left);
    }
    if (node.right) {
      lines.push({
        x1: node.x!,
        y1: node.y!,
        x2: node.right.x!,
        y2: node.right.y!,
        key: `${node.id}-${node.right.id}`,
      });
      traverseNodes(node.right);
    }
  };

  traverseNodes(root);

  return (
    <div className={styles.treeVizContainer}>
      <svg width="100%" height={svgHeight} viewBox={`-600 0 1200 ${viewBoxHeight}`}>
        {lines.map((line) => (
          <line
            key={line.key}
            x1={line.x1}
            y1={line.y1}
            x2={line.x2}
            y2={line.y2}
            className={styles.treeLine}
          />
        ))}
        {nodes.map((node) => (
          <g key={node.id}>
            <circle
              cx={node.x}
              cy={node.y}
              r="25"
              className={`${styles.treeNode} ${
                highlightedNodes.includes(node.value) ? styles.highlighted : ""
              }`}
            />
            <text x={node.x} y={node.y! + 5} className={styles.nodeLabel}>
              {node.value}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

export default BinaryTreeEngine;
